import { useState, type CSSProperties } from 'react'
import { useTranslation } from '../i18n'
import type { Team } from '../lib/types'
import { useTeamRoleLabel } from '../lib/roles'
import { COLORS } from '../lib/theme'
import { Icon } from './Icon'
import { PageHeader } from './atoms'
import { EstateTag } from './EstateTag'

function MetaStat({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          color: COLORS.outline,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 15, fontWeight: 700 }}>{value}</div>
    </div>
  )
}

function TeamCard({ team }: { team: Team }) {
  const { t } = useTranslation('teams')
  const roleLabel = useTeamRoleLabel()
  return (
    <div
      className="wh-card"
      style={{
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          height: 120,
          position: 'relative',
          background: 'linear-gradient(135deg, var(--nl-primary), var(--nl-primary-container))',
          overflow: 'hidden',
        }}
      >
        {team.photo ? (
          <>
            <img
              src={team.photo}
              alt={team.name}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            />
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.1) 60%)' }} />
          </>
        ) : (
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 400 120"
            style={{ position: 'absolute', inset: 0, opacity: 0.22 }}
          >
            {[...Array(8)].map((_, i) => (
              <path
                key={i}
                d={`M -20 ${20 + i * 18} Q 100 ${10 + i * 18} 200 ${25 + i * 18} T 420 ${15 + i * 18}`}
                fill="none"
                stroke="#fff"
                strokeWidth="1"
              />
            ))}
          </svg>
        )}
        {team.isEstate && (
          // Opposite the role pill, so an estate is spotted scanning down the row.
          <div style={{ position: 'absolute', top: 14, left: 14 }}>
            <EstateTag onDark />
          </div>
        )}
        <div style={{ position: 'absolute', top: 14, right: 14, display: 'flex', gap: 6 }}>
          {team.recommended && (
            <span
              className="wh-chip"
              style={{ background: COLORS.primary, color: COLORS.onPrimary }}
            >
              {t('list.recommended')}
            </span>
          )}
          <span
            className="wh-chip"
            // onPrimaryFixed, not primary: the plate below is a hard-coded
            // near-white that stays the same in both themes, so its text has to
            // be theme-independent as well. primary inverts with the palette —
            // dark green on light, pale green on dark — which left the role
            // unreadable on the plate at about 1.8:1 for dark-mode users.
            // Material's *-fixed tokens are identical on both palettes by
            // design, which is exactly what content on an unchanging surface
            // needs.
            style={{ background: 'rgba(255,255,255,0.92)', color: COLORS.onPrimaryFixed }}
          >
            {roleLabel(team.role)}
          </span>
        </div>
        <div style={{ position: 'absolute', bottom: 14, left: 16, right: 16, color: '#fff' }}>
          <div
            style={{
              fontSize: 11,
              opacity: 0.9,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              marginBottom: 4,
            }}
          >
            {team.area}
          </div>
          <div
            style={{
              fontFamily: COLORS.fontDisplay,
              fontSize: 22,
              fontWeight: 500,
            }}
          >
            {team.name}
          </div>
        </div>
      </div>
      <div
        style={{
          padding: 18,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          flex: 1,
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          <MetaStat label={t('list.stat.members')} value={team.members} />
          <MetaStat label={t('list.stat.area')} value={`${team.hectares} ha`} />
          <MetaStat label={t('list.stat.role')} value={roleLabel(team.role)} />
        </div>
        <div
          style={{
            fontSize: 12,
            color: COLORS.outline,
            padding: '8px 10px',
            background: COLORS.surface,
            borderRadius: 6,
          }}
        >
          <Icon name="hunts" size={12} /> &nbsp;{t('list.nextHunt')}{' '}
          {team.nextHunt ? (
            <b style={{ color: COLORS.onSurface }}>{team.nextHunt}</b>
          ) : (
            <span style={{ color: COLORS.outline }}>{t('noPlannedHunts')}</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
          <a
            href={`/teams/${team.id}`}
            className="wh-btn wh-btn-outline"
            style={{ flex: 1 }}
          >
            {t('list.open')}
          </a>
        </div>
      </div>
    </div>
  )
}

export interface TeamsPageProps {
  teams: Team[]
  /**
   * The team list could not be loaded. Renders a message and a retry in place of
   * the grid, and drops the member-count subtitle.
   *
   * An empty `teams` cannot carry this on its own: a failed request and an account
   * with genuinely no teams produce the same empty array, and the page then states
   * "you are a member of 0 teams" as a fact about the account. That reads as data
   * loss rather than a failure, so the user reports vanished teams instead of
   * retrying — and nothing on the page invites a retry, because nothing on it
   * looks broken.
   */
  loadFailed?: boolean
  /**
   * The team list is still in flight. Like {@link loadFailed}, an empty `teams`
   * cannot carry this on its own: while loading, `teams` is also `[]`, so without
   * this the page states "you are a member of 0 teams" — a fact about the account
   * — before the request has answered. Renders a loading placeholder and drops the
   * count subtitle until the real answer (a list, an empty account, or a failure)
   * is known.
   */
  loading?: boolean
  onRetry?: () => void
  /**
   * Create a team from the list. When provided, the "New team" button opens a
   * name dialog and calls this; rejecting shows the error inline. When omitted
   * (e.g. the prototype), the button is inert.
   */
  onCreateTeam?: (name: string) => Promise<void>
  /**
   * Join an existing team by its join code (CLUB-177). When provided, a "Join
   * team" action opens a code dialog and calls this; rejecting shows the error
   * inline. When omitted the action is hidden.
   */
  onJoinTeam?: (code: string) => Promise<void>
}

function NewTeamDialog({
  onCreate,
  onClose,
}: {
  onCreate: (name: string) => Promise<void>
  onClose: () => void
}) {
  const { t } = useTranslation('teams')
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const trimmed = name.trim()

  const submit = async () => {
    if (!trimmed || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      await onCreate(trimmed)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('create.error'))
      setSubmitting(false)
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('create.title')}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: COLORS.surface,
          borderRadius: 12,
          padding: 24,
          maxWidth: 360,
          width: '90%',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        }}
      >
        <p style={{ margin: '0 0 16px', color: COLORS.onSurface, fontSize: 16, fontWeight: 600 }}>
          {t('create.title')}
        </p>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, fontWeight: 600, color: COLORS.outline }}>
          {t('create.nameLabel')}
          <input
            className="wh-input"
            type="text"
            autoFocus
            value={name}
            placeholder={t('create.namePlaceholder')}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit() }}
          />
        </label>
        {error && (
          <div role="alert" style={{ marginTop: 10, fontSize: 12, color: COLORS.error }}>
            {error}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button className="wh-btn wh-btn-text" onClick={onClose} disabled={submitting}>
            {t('create.cancel')}
          </button>
          <button className="wh-btn wh-btn-primary" onClick={submit} disabled={!trimmed || submitting}>
            {submitting ? t('create.creating') : t('create.create')}
          </button>
        </div>
      </div>
    </div>
  )
}

function JoinTeamDialog({
  onJoin,
  onClose,
}: {
  onJoin: (code: string) => Promise<void>
  onClose: () => void
}) {
  const { t } = useTranslation('teams')
  const [code, setCode] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const trimmed = code.trim()

  const submit = async () => {
    if (!trimmed || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      await onJoin(trimmed)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('join.error'))
      setSubmitting(false)
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('join.title')}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: COLORS.surface,
          borderRadius: 12,
          padding: 24,
          maxWidth: 360,
          width: '90%',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        }}
      >
        <p style={{ margin: '0 0 6px', color: COLORS.onSurface, fontSize: 16, fontWeight: 600 }}>
          {t('join.title')}
        </p>
        <p style={{ margin: '0 0 16px', color: COLORS.outline, fontSize: 13 }}>
          {t('join.subtitle')}
        </p>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, fontWeight: 600, color: COLORS.outline }}>
          {t('join.codeLabel')}
          <input
            className="wh-input"
            type="text"
            autoFocus
            value={code}
            placeholder={t('join.codePlaceholder')}
            autoCapitalize="characters"
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => { if (e.key === 'Enter') submit() }}
          />
        </label>
        {error && (
          <div role="alert" style={{ marginTop: 10, fontSize: 12, color: COLORS.error }}>
            {error}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button className="wh-btn wh-btn-text" onClick={onClose} disabled={submitting}>
            {t('join.cancel')}
          </button>
          <button className="wh-btn wh-btn-primary" onClick={submit} disabled={!trimmed || submitting}>
            {submitting ? t('join.joining') : t('join.join')}
          </button>
        </div>
      </div>
    </div>
  )
}

const TEAM_GRID_STYLE: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
  gap: 16,
}

// Enough placeholders to fill the first row on a typical viewport without
// implying a specific team count — the real grid replaces them within a moment.
const SKELETON_CARD_COUNT = 3

// Screen-reader-only: the skeletons are aria-hidden, so the loading state needs
// a text node inside its live region to be announced. No visually-hidden utility
// class exists in the design system, so the canonical inline clip is used.
const SR_ONLY_STYLE: CSSProperties = {
  position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
  overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0,
}

// A card-shaped placeholder for the loading grid — static grey blocks (no
// animation), mirroring the skeleton pattern used elsewhere. aria-hidden: the
// loading state is announced by the page subtitle, not by each placeholder.
function TeamCardSkeleton() {
  const bar = (width: string): CSSProperties => ({
    height: 10, borderRadius: 999, background: COLORS.surfaceContainerHigh, width,
  })
  return (
    <div aria-hidden="true" className="wh-card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ width: 44, height: 44, borderRadius: 10, background: COLORS.surfaceContainerHigh, flexShrink: 0 }} />
        <span style={bar('55%')} />
      </div>
      <span style={bar('80%')} />
      <span style={bar('40%')} />
    </div>
  )
}

export function TeamsPage({ teams, loadFailed = false, loading = false, onRetry, onCreateTeam, onJoinTeam }: TeamsPageProps) {
  const { t } = useTranslation('teams')
  const [creating, setCreating] = useState(false)
  const [joining, setJoining] = useState(false)
  const subtitle = loadFailed
    ? undefined
    : loading
      ? t('list.loading')
      : t('list.subtitle', { count: teams.length })
  return (
    <div>
      <PageHeader
        title={t('list.title')}
        subtitle={subtitle}
        actions={
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {onJoinTeam && (
              <button
                className="wh-btn wh-btn-outline"
                onClick={() => setJoining(true)}
              >
                <Icon name="plus" size={16} />
                {t('list.joinTeam')}
              </button>
            )}
            <button
              className="wh-btn wh-btn-primary"
              onClick={() => { if (onCreateTeam) setCreating(true) }}
            >
              <Icon name="plus" size={16} />
              {t('list.newTeam')}
            </button>
          </div>
        }
      />
      {loadFailed ? (
        <div role="alert" className="wh-card" style={{ padding: 20, fontSize: 13, color: COLORS.onSurface }}>
          <div>{t('list.loadError')}</div>
          {onRetry ? (
            <button type="button" className="wh-btn-text" onClick={onRetry} style={{ marginTop: 8 }}>
              {t('list.retry')}
            </button>
          ) : null}
        </div>
      ) : loading ? (
        <div role="status" aria-live="polite" aria-busy="true">
          <span style={SR_ONLY_STYLE}>{t('list.loading')}</span>
          <div aria-hidden="true" style={TEAM_GRID_STYLE}>
            {Array.from({ length: SKELETON_CARD_COUNT }, (_, i) => (
              <TeamCardSkeleton key={i} />
            ))}
          </div>
        </div>
      ) : (
        <div style={TEAM_GRID_STYLE}>
          {teams.map((tm) => (
            <TeamCard key={tm.id} team={tm} />
          ))}
        </div>
      )}
      {creating && onCreateTeam && (
        <NewTeamDialog onCreate={onCreateTeam} onClose={() => setCreating(false)} />
      )}
      {joining && onJoinTeam && (
        <JoinTeamDialog onJoin={onJoinTeam} onClose={() => setJoining(false)} />
      )}
    </div>
  )
}
